import { forwardRef } from 'react';
import { GgcButton, type GgcButtonProps } from '@/components/ui/ggc/button';

/**
 * Primary action button rendered inside a dashboard table row (Edit / Details / …).
 *
 * Always carries `tag-slim-btn`, so it auto-hides until row hover unless the
 * "Always show buttons" table view option is enabled (see `.dashboard-table .tag-slim-btn`
 * in components.css). Use this for every in-row action button instead of a bare
 * GgcButton — that way the hover-gate can't be accidentally dropped when a button
 * is restyled or rewritten.
 *
 * Defaults to `variant="primary"` / `size="xs"`; override via props as needed.
 * Supports `asChild` (e.g. wrapping an <a>) — the class flows to the child.
 *
 * forwardRef: this is used as a PopoverShell/Radix Popover `trigger` (EmailCell,
 * TagEditPopover, InclusionCommentEditor) — Radix's `asChild`/Slot clones its
 * child and injects a ref to measure the anchor's position, which silently
 * fails ("Function components cannot be given refs") on a plain function
 * component. Forwarding the ref to the underlying GgcButton (itself forwardRef)
 * is what makes that anchoring actually work.
 */
export const RowActionButton = forwardRef<HTMLButtonElement, GgcButtonProps>(
  ({ className, variant = 'primary', size = 'xs', ...props }, ref) => (
    <GgcButton
      ref={ref}
      variant={variant}
      size={size}
      className={`tag-slim-btn${className ? ` ${className}` : ''}`}
      {...props}
    />
  ),
);
RowActionButton.displayName = 'RowActionButton';
